import {
  type Language,
  createUrlResolver,
  getLanguageFromLocale,
} from '@seek/melways-sites';
import React, { useState } from 'react';
import { createRouter } from '../urls';
import { environments } from 'src/siteConfig';
import type { AppConfig } from 'src/App/App';

type ProviderState = {
  router: ReturnType<typeof createRouter>;
  language: Language;
} & AppConfig;

const AppConfigContext = React.createContext<ProviderState | undefined>(
  undefined,
);

type ProviderProps = {
  children: React.ReactNode;
} & AppConfig;

export const AppConfigProvider = ({
  locale,
  site,
  environment,
  children,
}: ProviderProps) => {
  const [router] = useState(() =>
    createRouter({
      urlResolver: createUrlResolver({
        language: locale,
        site,
        staging: environment === environments.staging,
      }),
    }),
  );

  return (
    <AppConfigContext.Provider
      value={{
        router,
        locale,
        site,
        environment,
        language: getLanguageFromLocale(locale),
      }}
    >
      {children}
    </AppConfigContext.Provider>
  );
};

export const useAppConfig = () => {
  const context = React.useContext(AppConfigContext);

  if (!context) {
    throw new Error('useAppConfig must be used within a AppConfigProvider');
  }

  return context;
};
